/** A route file (KML) of a day or of the whole tour (B6): stored as a link, staged as a
 *  file token (the file rides with the save as a multipart part), or cleared. View mode
 *  shows the stored file the same way, in the same box. */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { useRef } from 'react';

import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatBytes, isStagedFile, type StagedFiles } from '@/features/records/fields/widgets/stagedFiles';

const ACCEPT = '.kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz';

function storedUrl(v: unknown): string | null {
  return typeof v === 'object' && v !== null && typeof (v as { url?: unknown }).url === 'string' ? (v as { url: string }).url : null;
}

export function KmlDisplay({ value }: { value: unknown }) {
  if (isStagedFile(value)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <InsertDriveFileOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary', flex: 'none' }} />
        <Typography variant="body2" noWrap title={`${value.name} — new file, ${formatBytes(value.size)}`} sx={{ minWidth: 0 }}>
          {value.name} — new file, {formatBytes(value.size)}
        </Typography>
      </Box>
    );
  }
  const url = storedUrl(value);
  if (!url) {
    return (
      <Typography variant="body2" color="text.secondary">
        No route file
      </Typography>
    );
  }
  return (
    <Box sx={{ minWidth: 0, overflow: 'hidden', display: 'flex' }}>
      <ValueActions actions={[act.copy(url, 'Copy the KML link')]}>
        <Link href={url} target="_blank" rel="noopener noreferrer" variant="body2" title={url} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <InsertDriveFileOutlinedIcon aria-hidden sx={{ fontSize: 16, flex: 'none' }} />
          <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Current route file
          </Box>
        </Link>
      </ValueActions>
    </Box>
  );
}

export function KmlControl({
  id,
  label,
  value,
  files,
  onChange,
}: {
  id: string;
  label: string;
  value: unknown;
  files: StagedFiles;
  onChange: (to: unknown) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const staged = isStagedFile(value) ? value : null;
  const missing = staged !== null && !files.has(staged);
  const hasFile = staged !== null || storedUrl(value) !== null;
  return (
    // One line: the file's name takes the room (cut with an ellipsis), the buttons keep
    // theirs — a day with a route file is as tall in edit mode as in view mode.
    <Box data-kml-control sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto', flexWrap: 'nowrap' }}>
      <Box sx={{ minWidth: 0, flex: '1 1 0', overflow: 'hidden' }}>
        {missing ? (
          <Typography variant="body2" color="error" noWrap title={`Re-attach ${staged.name} — a staged file does not survive a reload`}>
            Re-attach {staged.name} — a staged file does not survive a reload
          </Typography>
        ) : (
          <KmlDisplay value={value} />
        )}
      </Box>
      <Button id={id} size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => input.current?.click()} aria-label={`${hasFile ? 'Replace' : 'Upload'} ${label}`} sx={{ flex: 'none' }}>
        {hasFile ? 'Replace' : 'Upload'}
      </Button>
      <input
        ref={input}
        hidden
        type="file"
        accept={ACCEPT}
        data-kml-input
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onChange(files.stage(f));
          e.target.value = '';
        }}
      />
      {hasFile && (
        <Tooltip title={`Remove ${label} on save`}>
          <IconButton size="small" aria-label={`Remove ${label}`} onClick={() => onChange(null)} sx={{ flex: 'none' }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
